import { useEffect, useState } from 'react'
import { DataGridPro, GridToolbarQuickFilter } from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography, Grid } from '@mui/material';
/* REQUESTS */
import { gettingOvertime } from 'utils/Overtime'
/* ICONS */
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import ReplayIcon from '@mui/icons-material/Replay';
/* DIALOGS */
import { AddOvertime } from 'components/dialog/overtime/AddOvertime'
import { EditOvertime } from 'components/dialog/overtime/EditOvertime'
import { InfoOvertime } from 'components/dialog/overtime/InfoOvertime'
import { DeleteOvertime } from 'components/dialog/overtime/DeleteOvertime'
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting'

const ContextMenu = ({ contextMenu, setContextMenu, selectedRow, reloading, rows, setSelectedRow, setSelected }) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [openDelete, setOpenDelete] = useState(false)
    const basePath = buildSettingsBasePath('overtimes')
    const modalRoute = parseSettingsCrudRoute(location.pathname, 'overtimes')
    const openAdd = modalRoute.action === 'add'
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null
    const openInfo = modalRoute.action === 'detail' && modalRoute.id !== null
    const selectedRowFromRoute = modalRoute.id === null ? null : rows.find((row) => Number(row.id) === modalRoute.id)
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow

    const closeRouteModal = () => {
        navigate(basePath)
    }

    const options = [
        {
            name: t('general.buttons.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('overtimes', 'add'))
                setContextMenu(null)
            },
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false
        },
        {
            name: t('general.buttons.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('overtimes', 'update', current.id))
                setContextMenu(null)
            },
            divider: false,
            disabled: selectedRow.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            hidden: selectedRow.length === 1 ? false : true
        },
        {
            name: t('general.buttons.show'),
            icon: <InfoIcon />,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('overtimes', 'detail', current.id))
                setContextMenu(null)
            },
            divider: false,
            disabled: selectedRow.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            hidden: selectedRow.length <= 0 ? true : false
        },
        /* {
            name: t('general.buttons.duplicate'),
            icon: <CachedIcon />,
            divider: false,
            disabled: true,
            selected: false,
            onlyOne: true,
            hidden: selectedRow.length === 1 ? false : true
        }, */
        {
            name: t('general.buttons.delete'),
            icon: <DeleteIcon color='error' />,
            click: () => {
                setOpenDelete(true)
                setContextMenu(null)
            },
            divider: false,
            disabled: true,
            selected: true,
            onlyOne: false,
            hidden: false
        },
        {
            name: t('general.buttons.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading()
                setContextMenu(null)
            },
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false
        }
    ]

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                {options.map((option, index) => (
                    <MenuItem
                        aria-hidden={false}
                        key={index}
                        onClick={option.click}
                        disabled={option.disabled}
                        divider={option.divider}
                        hidden={option.hidden}
                    >
                        <ListItemIcon>
                            {option.icon}
                        </ListItemIcon>
                        <ListItemText primary={option.name} />
                    </MenuItem>
                ))}
            </Menu>
            <AddOvertime open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} reloading={reloading} rows={rows} setSelectedRow={setSelectedRow} setSelected={setSelected} />
            <EditOvertime open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} selectedRow={selectedRowsForRoute} reloading={reloading} rows={rows} setSelectedRow={setSelectedRow} setSelected={setSelected} />
            <InfoOvertime open={openInfo} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal() }} selectedRow={selectedRowsForRoute} setSelectedRow={setSelectedRow} setSelected={setSelected}/>
            <DeleteOvertime open={openDelete} setOpen={setOpenDelete} selectedRow={selectedRow} reloading={reloading} />
        </>
    )
}

const OvertimeTable = () => {
    const { t } = useTranslation()
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [contextMenu, setContextMenu] = useState(null)
    const [selectedRow, setSelectedRow] = useState([])
    const [selected, setSelected] = useState([])

    const columns = [
        { field: 'name', headerName: t('settings.overtimes.table.columns.name'), flex: 1 },
        { field: 'overtime_start', headerName: t('settings.overtimes.table.columns.overtime_start_after'), flex: 1, align: 'center', },
        { field: 'min_hour_calification', headerName: t('settings.overtimes.table.columns.min_hour_calification'), flex: 1, align: 'center', },
        {
            field: 'required_approval', headerName: t('settings.overtimes.table.columns.required_approval'), flex: 1, align: 'center',
            renderCell: (params) => {
                if (params.value) {
                    return <Typography sx={{ m: 2 }} variant="subtitle2">{t('general.buttons.yes')}</Typography>
                } else {
                    return <Typography sx={{ m: 2 }} variant="subtitle2">{t('general.buttons.no')}</Typography>
                }
            }
        }
    ]

    const reloading = () => {
        if (JSON.parse(localStorage.getItem('permissions')).find(permision => permision === 'timekeeper.OvertimeRules')) {
            gettingOvertime(setLoading, setRows)
        } else {
            setRows([])
            setLoading(false)
        }
    }

    useEffect(() => {
        reloading()
    }, [])

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault()
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                rows={rows}
                columns={columns}
                pagination
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                slots={{
                    toolbar: () => {
                        return (
                            <Grid container justifyContent="flex-end" sx={{ bgcolor: 'background.paper', pt: 1, pb: 0.5 ,px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <GridToolbarQuickFilter color='primary' variant='outlined' size='small'/>
                            </Grid>
                        )
                    },
                    noRowsOverlay: () => 'No hay datos para mostrar'
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection) => {
                    let row = rows.filter((row) => newSelection.includes(row.id))
                    setSelected(row)
                    setSelectedRow(newSelection)
                }}
                rowSelectionModel={selectedRow}
                loading={loading}
                localeText={{
                    toolbarQuickFilterPlaceholder: t('general.table.headerFilters.toolbarQuickFilterPlaceholder'),
                    toolbarQuickFilterDeleteIconLabel: t('general.table.headerFilters.toolbarQuickFilterDeleteIconLabel'),
                    footerRowSelected: (count) =>
                        count > 1
                            ? t('general.table.footerFilters.footerRowSelected.>1', { count: count.toLocaleString() })
                            : t('general.table.footerFilters.footerRowSelected.=1', { count: count.toLocaleString() }),
                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) =>
                            t('general.table.footerFilters.labelDisplayedRows', { from: from.toLocaleString(), to: to.toLocaleString(), count: count.toLocaleString() }),
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    }

                }}
            />
            <ContextMenu
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
                selectedRow={selected}
                rows={rows}
                setSelectedRow={setSelectedRow}
                setSelected={setSelected}
            />
        </div>
    )
}

export { OvertimeTable }